import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { fmtDate } from "@/lib/time";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  pending:  "bg-amber-500/10 text-amber-400 border border-amber-500/20",
  approved: "bg-blue-500/10 text-blue-400 border border-blue-500/20",
  paid:     "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20",
  rejected: "bg-red-500/10 text-red-400 border border-red-500/20",
};

const FILTERS = ["all", "pending", "approved", "paid", "rejected"];

export default function PartnerCommissions() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (filter !== "all") params.append("status", filter);
      const res = await axios.get(`${API}/api/sm/partners/portal/commissions?${params}`, { withCredentials: true });
      setItems(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [page, filter]);

  useEffect(() => { setPage(1); }, [filter]);
  useEffect(() => { load(); }, [load]);

  const totalPages = Math.ceil(total / 20);

  return (
    <div className="space-y-6" data-testid="partner-commissions-page">
      <div>
        <h1 className="font-serif text-3xl text-foreground font-light">My Earnings</h1>
        <p className="text-sm text-muted-foreground mt-1">{total} total earning{total !== 1 ? "s" : ""}</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2" data-testid="commission-filters">
        {FILTERS.map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-all ${
              filter === f ? "bg-primary text-primary-foreground" : "bg-white/5 text-muted-foreground hover:bg-white/10"
            }`}>
            {f === "all" ? "All" : f}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        {loading ? (
          <div className="space-y-3 p-5">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-10 bg-white/5 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <div className="text-2xl mb-2">₹</div>
            <div className="text-sm">No earnings found</div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left px-5 py-3.5 text-[11px] uppercase tracking-[0.1em] text-muted-foreground font-medium">Source</th>
                <th className="text-left px-4 py-3.5 text-[11px] uppercase tracking-[0.1em] text-muted-foreground font-medium">Date</th>
                <th className="text-left px-4 py-3.5 text-[11px] uppercase tracking-[0.1em] text-muted-foreground font-medium">Status</th>
                <th className="text-right px-5 py-3.5 text-[11px] uppercase tracking-[0.1em] text-muted-foreground font-medium">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {items.map(c => (
                <tr key={c.id} className="hover:bg-white/[0.02] transition-colors">
                  <td className="px-5 py-3.5 text-gray-300">{c.source || "Referral"}</td>
                  <td className="px-4 py-3.5 text-muted-foreground text-xs">
                    {c.created_at ? fmtDate(c.created_at) : "—"}
                  </td>
                  <td className="px-4 py-3.5">
                    <span className={`text-[10px] px-2.5 py-0.5 rounded-full font-medium capitalize ${STATUS_BADGE[c.status] || "bg-gray-500/10 text-muted-foreground"}`}>
                      {c.status}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-right font-medium text-foreground">
                    ₹{(c.amount || 0).toLocaleString("en-IN")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="px-3 py-1.5 text-xs rounded-lg bg-white/5 text-muted-foreground hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed">
              Prev
            </button>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="px-3 py-1.5 text-xs rounded-lg bg-white/5 text-muted-foreground hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed">
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
